import React from "react";
import { connect } from "react-redux";
import { DiagramQuestion as Component } from "../DiagramQuestion";
import { submitSelections } from "./store/actions";
import { fetchTranslations } from "../locales/config";
import TranslationsProvider from "../../TranslationsProvider";

interface Selection {
  index: number;
  value: string;
}

interface DropDown {
  answer: string;
  top: string;
  left: string;
  ariaLabel?: string;
}

interface DiagramContent {
  dropDowns: DropDown[];
  image: { [key: string]: any; altText: string };
}

interface OwnProps {
  id: string;
  diagram?: DiagramContent;
  [key: string]: any;
}

interface StateProps {
  selections: Selection[];
  complete: boolean;
}

interface DispatchProps {
  submitSelections: (payload: {
    componentID: string;
    selections: Selection[];
  }) => void;
}

type DiagramQuestionReduxProps = OwnProps & StateProps & DispatchProps;

interface ReduxState {
  diagramQuestion: {
    [key: string]: {
      selections: Selection[];
    };
  };
}

/**
 * mapStateToProps - This function maps the state from the Redux store to the component's props.
 * It allows us to extract specific data from the Redux store state and use them as props within our React component.
 *
 * @param {Object} state - The entire state of the Redux store.
 * @param {Object} ownProps - The current props of the component.
 *
 * @returns {Object} - Returns an object which populates the props of our component.
 */
function mapStateToProps(
  state: ReduxState,
  ownProps: OwnProps
): OwnProps & StateProps {
  // Find the entry in the store state using the provided id.
  const entry = state.diagramQuestion[ownProps.id];

  if (!entry) {
    // If no matching entry is found, return the original ownProps.
    return {
      ...ownProps,
      selections: [],
      complete: false,
    };
  }

  const dropDowns = ownProps.diagram?.dropDowns || [];

  const isComplete = dropDowns.length
    ? dropDowns.every((dropDown, index) => {
        const selection = entry.selections.find((s) => s.index === index);
        return selection?.value === dropDown.answer;
      })
    : false;

  return {
    ...ownProps,
    selections: entry.selections,
    complete: isComplete,
  };
}

/**
 * mapDispatchToProps - Maps dispatch actions to the component's props.
 * This allows our React component to dispatch actions to modify the state in the Redux store.
 *
 * @param {Function} dispatch - The dispatch function from the Redux store.
 *
 * @returns {Object} - Returns an object with dispatch action functions.
 */
function mapDispatchToProps(dispatch: any): DispatchProps {
  return {
    // Dispatch the submitSelections action.
    submitSelections: (payload) => {
      dispatch(submitSelections(payload));
    },
  };
}

// Connect our component to the Redux store.
// This HOC (Higher Order Component) provides our component with props from the store state (mapStateToProps) and dispatch actions (mapDispatchToProps).
export const DiagramQuestionRedux = connect(
  mapStateToProps,
  mapDispatchToProps
)((props: DiagramQuestionReduxProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <Component {...props} />
  </TranslationsProvider>
));
